import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell,
} from "recharts";
import { CalendarRange } from "lucide-react";
import Header from "@/components/Header";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatEuro, monthKey, monthLabel } from "@/lib/format";
import { toast } from "sonner";

const PALETTE = ["#2563eb", "#f43f5e", "#f97316", "#10b981", "#8b5cf6", "#eab308"];
const ESTADO_COLORS = {
  "Orçamento": "#64748b", "Pedido": "#2563eb", "Adjudicado": "#f59e0b",
  "Realizado": "#10b981", "Recusado": "#f43f5e",
};

function aggregate(events) {
  let total_valor = 0, total_custos = 0, total_ci = 0, total_margem = 0, total_lm = 0;
  const estMap = {}, comMap = {};
  for (const d of events) {
    total_valor += d.valor_total || 0;
    total_custos += d.custos || 0;
    total_ci += d.custos_indiretos || 0;
    total_margem += d.margem || 0;
    total_lm += d.lucro_comercial_valor || 0;
    const est = d.estado || "Orçamento";
    const e = estMap[est] || (estMap[est] = { estado: est, count: 0, valor_total: 0, margem: 0 });
    e.count += 1; e.valor_total += d.valor_total || 0; e.margem += d.margem || 0;
    const slots = [
      ["lucro_comercial_nome", "lucro_comercial_valor"],
      ["comercial_1_nome", "comercial_1_valor"],
      ["comercial_2_nome", "comercial_2_valor"],
      ["comercial_3_nome", "comercial_3_valor"],
    ];
    for (const [nk, vk] of slots) {
      const nome = (d[nk] || "").trim();
      if (nome) {
        const c = comMap[nome] || (comMap[nome] = { nome, total: 0, count: 0 });
        c.total += d[vk] || 0; c.count += 1;
      }
    }
  }
  return {
    total_valor, total_custos, total_custos_indiretos: total_ci, total_margem,
    total_lucro_mundideias: total_lm, total_mundideias: total_lm + total_ci,
    count: events.length,
    by_estado: Object.values(estMap),
    by_comercial: Object.values(comMap).sort((a, b) => b.total - a.total),
  };
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <div className="mb-4">
        <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      <div className="h-[300px] w-full">{children}</div>
    </motion.div>
  );
}

const tipStyle = { borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" };
const kfmt = (v) => `${(v / 1000).toLocaleString("de-DE")}k`;

export default function Analytics() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("all");

  useEffect(() => {
    api.get("/events")
      .then((r) => setEvents(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const months = useMemo(() => {
    const set = new Set();
    events.forEach((e) => { const k = monthKey(e.data_evento); if (k) set.add(k); });
    return Array.from(set).sort().reverse();
  }, [events]);

  const filtered = useMemo(() => {
    if (month === "all") return events;
    return events.filter((e) => monthKey(e.data_evento) === month);
  }, [events, month]);

  const stats = useMemo(() => aggregate(filtered), [filtered]);

  const financeData = [
    { nome: "Faturação", valor: stats.total_valor },
    { nome: "Custos", valor: stats.total_custos },
    { nome: "Custos Ind.", valor: stats.total_custos_indiretos },
    { nome: "Margem", valor: stats.total_margem },
    { nome: "Lucro Mund.", valor: stats.total_lucro_mundideias },
    { nome: "Mund.+C.Ind.", valor: stats.total_mundideias },
  ];

  const empty = !loading && stats.count === 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Gráficos & Análise</h1>
            <p className="text-sm text-muted-foreground mt-1">Faturação, custos, margens e lucros — filtre por mês do orçamento.</p>
          </div>
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-muted-foreground" />
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[200px]" data-testid="analytics-month-select">
                <SelectValue placeholder="Mês" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os meses</SelectItem>
                {months.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`analytics-month-${m}`}>{monthLabel(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="text-xs text-muted-foreground" data-testid="analytics-count">
          {stats.count} processo(s) {month === "all" ? "no total" : `em ${monthLabel(month)}`}
        </div>

        {empty ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground" data-testid="analytics-empty">
            Sem dados para o período selecionado.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Resumo Financeiro" subtitle="Totais do período (€)" testid="chart-finance">
              <ResponsiveContainer>
                <BarChart data={financeData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={64} tickFormatter={kfmt} />
                  <Tooltip formatter={(v) => formatEuro(v)} contentStyle={tipStyle} />
                  <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                    {financeData.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Eventos por Estado" subtitle="Distribuição por nº de processos" testid="chart-estado-pie">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={stats.by_estado} dataKey="count" nameKey="estado" cx="50%" cy="50%" outerRadius={100}
                    label={(e) => `${e.estado} (${e.count})`} labelLine={false}>
                    {stats.by_estado.map((e, i) => <Cell key={i} fill={ESTADO_COLORS[e.estado] || PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Faturação por Estado" subtitle="Valor de venda (€) por estado" testid="chart-estado-bar">
              <ResponsiveContainer>
                <BarChart data={stats.by_estado} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="estado" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={64} tickFormatter={kfmt} />
                  <Tooltip formatter={(v) => formatEuro(v)} contentStyle={tipStyle} />
                  <Bar dataKey="valor_total" name="Faturação" radius={[6, 6, 0, 0]}>
                    {stats.by_estado.map((e, i) => <Cell key={i} fill={ESTADO_COLORS[e.estado] || PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Lucro por Comercial" subtitle="Total acumulado por nome (€)" testid="chart-comercial">
              {stats.by_comercial.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem comerciais atribuídos.</div>
              ) : (
                <ResponsiveContainer>
                  <BarChart data={stats.by_comercial} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={kfmt} />
                    <YAxis type="category" dataKey="nome" width={110} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip formatter={(v) => formatEuro(v)} contentStyle={tipStyle} />
                    <Bar dataKey="total" name="Lucro" radius={[0, 6, 6, 0]}>
                      {stats.by_comercial.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        )}
      </main>
    </div>
  );
}
